import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Alert,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  CardActions,
  Tabs,
  Tab,
  Badge,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Search,
  Edit,
  PlayArrow,
  CheckCircle,
  Schedule,
  Assignment,
  Person,
  CalendarToday,
  AccessTime,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import { useAuth } from '../contexts/AuthContext.tsx';
import { surveysApi, projectsApi, usersApi } from '../services/api.ts';
import { Survey, SurveyStatus, SurveyCreate } from '../types/index.ts';

// Add survey dialog component
interface AddSurveyDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: (survey: SurveyCreate) => void;
}

const AddSurveyDialog: React.FC<AddSurveyDialogProps> = ({ open, onClose, onAdd }) => {
  const { control, handleSubmit, reset, formState: { errors } } = useForm<SurveyCreate>();
  const { data: projects } = useQuery('projects', () => projectsApi.getProjects());
  const { data: surveyors } = useQuery('surveyors', () => usersApi.getAvailableSurveyors());

  const onSubmit = (data: SurveyCreate) => {
    onAdd(data);
    reset();
    onClose();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Schedule New Survey</DialogTitle>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Controller
                  name="title"
                  control={control}
                  rules={{ required: 'Title is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Survey Title"
                      fullWidth
                      error={!!errors.title}
                      helperText={errors.title?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="project_id"
                  control={control}
                  rules={{ required: 'Project is required' }}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.project_id}>
                      <InputLabel>Project</InputLabel>
                      <Select {...field} label="Project">
                        {projects?.projects.map((project) => (
                          <MenuItem key={project.id} value={project.id}>
                            {project.title} - {project.project_number}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="surveyor_id"
                  control={control}
                  rules={{ required: 'Surveyor is required' }}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.surveyor_id}>
                      <InputLabel>Surveyor</InputLabel>
                      <Select {...field} label="Surveyor">
                        {surveyors?.map((surveyor) => (
                          <MenuItem key={surveyor.id} value={surveyor.id}>
                            {surveyor.full_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="scheduled_date"
                  control={control}
                  render={({ field }) => (
                    <DateTimePicker
                      label="Scheduled Date & Time"
                      value={field.value ? new Date(field.value) : null}
                      onChange={(newValue) => {
                        field.onChange(newValue?.toISOString());
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.scheduled_date,
                          helperText: errors.scheduled_date?.message,
                        },
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="notes"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Notes"
                      multiline
                      rows={3}
                      fullWidth
                    />
                  )}
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="submit" variant="contained">
              Schedule Survey
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </LocalizationProvider>
  );
};

// Survey card component for calendar view
interface SurveyCardProps {
  survey: Survey;
  onStart: (surveyId: number) => void;
  onComplete: (surveyId: number) => void;
}

const SurveyCard: React.FC<SurveyCardProps> = ({ survey, onStart, onComplete }) => {
  const { user, isSurveyor } = useAuth();

  const getStatusColor = (status: SurveyStatus) => {
    switch (status) {
      case SurveyStatus.COMPLETED:
        return 'success';
      case SurveyStatus.IN_PROGRESS:
        return 'warning';
      case SurveyStatus.CANCELLED:
        return 'error';
      default:
        return 'primary';
    }
  };

  const canStart = isSurveyor && survey.surveyor_id === user?.id && survey.status === SurveyStatus.SCHEDULED;
  const canComplete = isSurveyor && survey.surveyor_id === user?.id && survey.status === SurveyStatus.IN_PROGRESS;

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography variant="h6" component="h3" sx={{ fontSize: '1rem' }}>
            {survey.title}
          </Typography>
          <Chip
            label={survey.status_display}
            color={getStatusColor(survey.status)}
            size="small"
          />
        </Box>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          {survey.survey_number}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <CalendarToday sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="body2">
            {survey.scheduled_date
              ? new Date(survey.scheduled_date).toLocaleString()
              : 'No date scheduled'}
          </Typography>
        </Box>

        {survey.duration_minutes > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <AccessTime sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2">
              Duration: {survey.duration_minutes} minutes
            </Typography>
          </Box>
        )}

        {survey.notes && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {survey.notes}
          </Typography>
        )}
      </CardContent>

      {(canStart || canComplete) && (
        <CardActions>
          {canStart && (
            <Button
              size="small"
              startIcon={<PlayArrow />}
              onClick={() => onStart(survey.id)}
            >
              Start Survey
            </Button>
          )}
          {canComplete && (
            <Button
              size="small"
              startIcon={<CheckCircle />}
              color="success"
              onClick={() => onComplete(survey.id)}
            >
              Complete Survey
            </Button>
          )}
        </CardActions>
      )}
    </Card>
  );
};

const SurveysPage: React.FC = () => {
  const { user, isManager, isSurveyor } = useAuth();
  const queryClient = useQueryClient();

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<SurveyStatus | 'all'>('all');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');

  // Fetch surveys with filters
  const { data: surveysData, isLoading, error } = useQuery(
    ['surveys', { status: statusFilter !== 'all' ? statusFilter : undefined }],
    () => surveysApi.getSurveys({
      status: statusFilter !== 'all' ? statusFilter : undefined,
    }),
    {
      refetchOnWindowFocus: false,
    }
  );

  // Add survey mutation
  const addSurveyMutation = useMutation(
    (survey: SurveyCreate) => surveysApi.createSurvey(survey),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('surveys');
      },
    }
  );

  // Start survey mutation
  const startSurveyMutation = useMutation(
    (surveyId: number) => surveysApi.startSurvey(surveyId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('surveys');
      },
    }
  );

  // Complete survey mutation
  const completeSurveyMutation = useMutation(
    (surveyId: number) => surveysApi.completeSurvey(surveyId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('surveys');
      },
    }
  );

  const handleAddSurvey = (survey: SurveyCreate) => {
    addSurveyMutation.mutate(survey);
  };

  const handleStartSurvey = (surveyId: number) => {
    startSurveyMutation.mutate(surveyId);
  };

  const handleCompleteSurvey = (surveyId: number) => {
    completeSurveyMutation.mutate(surveyId);
  };

  // Filter surveys based on search term
  const filteredSurveys = React.useMemo(() => {
    if (!surveysData?.surveys) return [];

    return surveysData.surveys.filter((survey) =>
      survey.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      survey.survey_number.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [surveysData?.surveys, searchTerm]);

  // Group surveys by status for stats
  const surveysByStatus = React.useMemo(() => {
    if (!surveysData?.surveys) return {};

    return surveysData.surveys.reduce((acc, survey) => {
      if (!acc[survey.status]) {
        acc[survey.status] = [];
      }
      acc[survey.status].push(survey);
      return acc;
    }, {} as Record<SurveyStatus, Survey[]>);
  }, [surveysData?.surveys]);

  if (error) {
    return (
      <Alert severity="error">
        Failed to load surveys. Please try refreshing the page.
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Surveys
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {surveysData?.total || 0} total surveys
          </Typography>
        </Box>
        {(isManager || isSurveyor) && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setAddDialogOpen(true)}
          >
            Schedule Survey
          </Button>
        )}
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Badge badgeContent={surveysByStatus[SurveyStatus.SCHEDULED]?.length || 0} color="primary">
                <Schedule sx={{ fontSize: 40, color: 'primary.main' }} />
              </Badge>
              <Typography variant="h6" sx={{ mt: 1 }}>
                Scheduled
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Badge badgeContent={surveysByStatus[SurveyStatus.IN_PROGRESS]?.length || 0} color="warning">
                <Assignment sx={{ fontSize: 40, color: 'warning.main' }} />
              </Badge>
              <Typography variant="h6" sx={{ mt: 1 }}>
                In Progress
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Badge badgeContent={surveysByStatus[SurveyStatus.COMPLETED]?.length || 0} color="success">
                <CheckCircle sx={{ fontSize: 40, color: 'success.main' }} />
              </Badge>
              <Typography variant="h6" sx={{ mt: 1 }}>
                Completed
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Person sx={{ fontSize: 40, color: 'text.secondary' }} />
              <Typography variant="h6" sx={{ mt: 1 }}>
                My Surveys
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isSurveyor ? filteredSurveys.filter(s => s.surveyor_id === user?.id).length : 'N/A'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search surveys by title or number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value as SurveyStatus | 'all')}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value={SurveyStatus.SCHEDULED}>Scheduled</MenuItem>
                <MenuItem value={SurveyStatus.IN_PROGRESS}>In Progress</MenuItem>
                <MenuItem value={SurveyStatus.COMPLETED}>Completed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Tabs value={viewMode} onChange={(_, value) => setViewMode(value)}>
              <Tab label="List" value="list" />
              <Tab label="Cards" value="calendar" />
            </Tabs>
          </Grid>
        </Grid>
      </Paper>

      {/* Survey List/Cards */}
      {isLoading ? (
        <Grid container spacing={2}>
          {Array.from({ length: 6 }).map((_, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Skeleton variant="rectangular" height={200} />
            </Grid>
          ))}
        </Grid>
      ) : filteredSurveys.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Assignment sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            {searchTerm || statusFilter !== 'all' ? 'No surveys found matching your criteria.' : 'No surveys scheduled yet.'}
          </Typography>
          {(isManager || isSurveyor) && (
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={() => setAddDialogOpen(true)}
              sx={{ mt: 2 }}
            >
              Schedule First Survey
            </Button>
          )}
        </Paper>
      ) : viewMode === 'calendar' ? (
        <Grid container spacing={2}>
          {filteredSurveys.map((survey) => (
            <Grid item xs={12} sm={6} md={4} key={survey.id}>
              <SurveyCard
                survey={survey}
                onStart={handleStartSurvey}
                onComplete={handleCompleteSurvey}
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Survey Details</TableCell>
                <TableCell>Project</TableCell>
                <TableCell>Scheduled</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSurveys.map((survey) => (
                <TableRow key={survey.id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {survey.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {survey.survey_number}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      Project ID: {survey.project_id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {survey.scheduled_date
                        ? new Date(survey.scheduled_date).toLocaleString()
                        : 'Not scheduled'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={survey.status_display}
                      color={
                        survey.status === SurveyStatus.COMPLETED ? 'success' :
                        survey.status === SurveyStatus.IN_PROGRESS ? 'warning' : 'primary'
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {survey.duration_minutes > 0 ? `${survey.duration_minutes} min` : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {isSurveyor && survey.surveyor_id === user?.id && (
                      <>
                        {survey.status === SurveyStatus.SCHEDULED && (
                          <Tooltip title="Start Survey">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleStartSurvey(survey.id)}
                            >
                              <PlayArrow />
                            </IconButton>
                          </Tooltip>
                        )}
                        {survey.status === SurveyStatus.IN_PROGRESS && (
                          <Tooltip title="Complete Survey">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleCompleteSurvey(survey.id)}
                            >
                              <CheckCircle />
                            </IconButton>
                          </Tooltip>
                        )}
                      </>
                    )}
                    <Tooltip title="View Details">
                      <IconButton size="small">
                        <Edit />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add Survey Dialog */}
      <AddSurveyDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onAdd={handleAddSurvey}
      />
    </Box>
  );
};

export default SurveysPage;